import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useAnimate } from 'motion/react';
import { prefersReducedMotion } from '../../lib/motion';
import { md } from '../../lib/md';
import { sound } from '../../lib/sound';
import { useLocale } from '../../state/useLocale';
import type { TutorialTry } from '../../types';

/**
 * One rep at a simulated prompt.
 *
 * The learner types the card's command and sees what it would print. Nothing
 * runs: the output is authored, which is what lets a card answer instantly,
 * stay correct for ever, and appear while the level's own workspace is still
 * being seeded. It is practice, so a miss costs nothing — there is no score
 * here, and no count of attempts is kept anywhere.
 *
 * The prompt matches the guest's own (sandbox/bashrc) character for
 * character: `engineer@lattice:<folder>[ (branch)] $`. What it does not
 * borrow is the guest's ANSI colour. The real shell paints the user green,
 * the host blue and the folder cyan; this product has one colour and spends
 * it on what is live, which here is the caret and nothing else.
 *
 * None of that is translated, in either direction. The prompt is the guest's,
 * the command is typed on an English keyboard, and `output:` is what an
 * English Linux prints (docs/i18n.md §6.1) — so the screen carries an
 * explicit `dir="ltr"` and keeps it in both languages, rather than relying on
 * a stylesheet to hold it. Only the chrome around it — the two words above
 * the screen, the fallback nudge, the name of an empty answer — is a
 * sentence, and only those come from the catalogue. The card's own `note` and
 * `nudge` are Arabic when the mission has an Arabic sidecar, and they are
 * given the current prose direction explicitly. `auto` is not enough here:
 * an Arabic hint that starts with a command would otherwise see that first
 * Latin run and align the whole sentence left-to-right.
 */

/** Matching the house typing feel (ui/index.tsx, `Typed`) without its CSS width trick. */
const TYPE_MS = 28;
/** A breath between the last character and Enter, so it reads as a person. */
const SUBMIT_AFTER_MS = 220;

/** What the learner typed versus what the author wrote: spacing is not the lesson. */
const collapse = (text: string) => text.trim().replace(/\s+/g, ' ');

export interface TryPromptProps {
  /** The card's rep. `try` is a reserved word, so it arrives under a plain name. */
  rep: TutorialTry;
  /** What the prompt shows between the colon and the `$`. */
  cwd: string;
  /** True when this card's rep was already passed earlier in the session. */
  passed: boolean;
  /** `how`: typed by the learner, or by Show me; `misses`: wrong tries before it. */
  onPass(how: 'typed' | 'shown', misses: number): void;
  /**
   * Focus the prompt when the card mounts. True in the warm-up, where the card
   * arriving is the learner asking for it; false on the landing page, where
   * focusing on load would open a phone's keyboard and scroll the reader past
   * the headline before they have read it.
   */
  autoFocus?: boolean;
}

export function TryPrompt({ rep, cwd, passed, onPass, autoFocus = true }: TryPromptProps) {
  const { dir, strings } = useLocale();
  const s = strings.warmup.rep;
  const [value, setValue] = useState('');
  /** The lines that did not match, echoed back the way a shell would. */
  const [misses, setMisses] = useState<string[]>([]);
  /** The line that did match. Null until it does; the rep is then over. */
  const [accepted, setAccepted] = useState<string | null>(passed ? rep.run : null);
  const [typing, setTyping] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const screenRef = useRef<HTMLDivElement>(null);
  const timer = useRef(0);
  /** Set by Show me, so a pass can say who typed it. */
  const shown = useRef(false);
  const [line, animate] = useAnimate<HTMLFormElement>();

  const prompt = `engineer@lattice:${cwd} $ `;
  const done = accepted !== null;

  /**
   * Built once per card, inside a try/catch. `accept:` is author-written and
   * validated as a string, not as a pattern, so a regex that does not compile
   * has to degrade to the exact comparison rather than throw while a card is
   * rendering.
   */
  const matches = useMemo(() => {
    const exact = (text: string) => collapse(text) === collapse(rep.run);
    if (!rep.accept) return exact;
    try {
      const re = new RegExp(rep.accept);
      return (text: string) => re.test(collapse(text));
    } catch {
      return exact;
    }
  }, [rep.accept, rep.run]);

  // Mount only. The card is keyed on its step, so mounting is this card
  // arriving on screen, and the thing to do when it arrives is offer the
  // prompt. The cleanup belongs here too: it must cancel a "Show me" that is
  // still typing when the learner moves on.
  useEffect(() => {
    if (!passed && autoFocus) inputRef.current?.focus();
    return () => window.clearTimeout(timer.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The scrollback behaves like one: the newest line is the one you can see.
  useEffect(() => {
    const el = screenRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [misses.length, accepted]);

  const run = useCallback(
    (text: string) => {
      const typed = text.trim();
      if (!typed || accepted !== null) return;
      setValue('');
      if (matches(typed)) {
        setAccepted(typed);
        sound.objective();
        onPass(shown.current ? 'shown' : 'typed', misses.length);
        return;
      }
      setMisses((prev) => [...prev, typed]);
      // A command outside this guided example is not a verdict from bash.
      sound.click();
      // Two oscillations of about four pixels: the same "no" the level list
      // gives a locked row, short enough to be over before the nudge is read.
      if (!prefersReducedMotion() && line.current) {
        void animate(line.current, { x: [0, -4, 4, -4, 4, 0] }, { duration: 0.22, ease: 'easeInOut' });
      }
      inputRef.current?.focus();
    },
    [accepted, matches, onPass, animate, line, misses.length],
  );

  const submit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      // While "Show me" is mid-line the input belongs to it, not to Enter.
      if (typing) return;
      run(value);
    },
    [run, value, typing],
  );

  /**
   * The product typing on the learner's behalf, for anyone who would rather
   * watch the command than compose it. It ends by pressing Enter, so the
   * card resolves the same way it would have by hand.
   */
  const showMe = useCallback(() => {
    if (typing || done) return;
    shown.current = true;
    inputRef.current?.focus();
    if (prefersReducedMotion()) {
      setValue(rep.run);
      run(rep.run);
      return;
    }
    setTyping(true);
    setValue('');
    let i = 0;
    const tick = () => {
      i += 1;
      setValue(rep.run.slice(0, i));
      if (i < rep.run.length) {
        timer.current = window.setTimeout(tick, TYPE_MS);
      } else {
        timer.current = window.setTimeout(() => {
          setTyping(false);
          run(rep.run);
        }, SUBMIT_AFTER_MS);
      }
    };
    timer.current = window.setTimeout(tick, TYPE_MS);
  }, [typing, done, rep.run, run]);

  return (
    <div className="try">
      <div className="try-head">
        <span className="label">{s.head}</span>
        {!done && (
          <button type="button" className="try-show" onClick={showMe} disabled={typing}>
            {s.show}
          </button>
        )}
      </div>

      {/* A simulated screen, so it is held the way the real one is: the
          prompt, the command and the output are all the machine's, and a
          path read right to left is not a path. */}
      <p className="try-scope note" dir={dir}>{s.scope}</p>
      <div className="try-screen scroll" dir="ltr" ref={screenRef}>
        {misses.map((text, i) => (
          <p className="try-line" key={i}>
            <span className="try-prompt">{prompt}</span>
            <span className="try-typed">{text}</span>
          </p>
        ))}

        {/* One nudge, under the last miss rather than under each: the shell
            repeats itself, a mentor does not. */}
        {!done && misses.length > 0 && (
          <div className="try-nudge" dir={dir} role="status">
            <p>{s.exampleOnly}</p>
            <code dir="ltr">{rep.run}</code>
            <div dangerouslySetInnerHTML={md(rep.nudge ?? s.nudge)} />
          </div>
        )}

        {done ? (
          <>
            <p className="try-line">
              <span className="try-prompt">{prompt}</span>
              <span className="try-typed">{accepted}</span>
            </p>
            {/* An empty `output:` is a real answer — `echo … > FILE` prints
                nothing, and that is the whole point of that card. Left as a
                blank gap it reads as something that failed to load, so the
                silence is named instead. */}
            {rep.output.trim() ? (
              <pre className="try-out">{rep.output.replace(/\n+$/, '')}</pre>
            ) : (
              <p className="try-out is-silent">{s.silent}</p>
            )}
          </>
        ) : (
          <form className="try-line is-live" onSubmit={submit} ref={line}>
            {/* Chrome for the eye only: the input below carries the same text
                and is what a screen reader reads, so this would be said
                twice. */}
            <span className="try-prompt" aria-hidden="true">
              {prompt}
            </span>
            <span className="try-typed" aria-hidden="true">
              {value}
            </span>
            <span className="try-caret caret-live" aria-hidden="true" />
            {/* A real input, laid over the whole line so a click lands in it,
                but drawn by the spans above: a terminal has no field, no
                border and no caret of its own. */}
            <input
              ref={inputRef}
              className="try-input"
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              readOnly={typing}
              spellCheck={false}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              aria-label={s.inputLabel}
            />
          </form>
        )}
      </div>

      {done && rep.note && <div className="try-note prose" dir={dir} dangerouslySetInnerHTML={md(rep.note)} />}
    </div>
  );
}

export default TryPrompt;
